import { useState, useEffect } from 'react';

interface WorksheetProgress {
  [key: string]: boolean;
}

interface Worksheet {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
}

const worksheets: Worksheet[] = [
  // Polynomials & Polynomial Functions
  { id: 'g11-poly-ws-1', title: 'Polynomial Operations & Long Division', description: 'Practice adding, subtracting, multiplying polynomials and polynomial long division', topic: 'Polynomials', difficulty: 'Medium', pages: 4 },
  { id: 'g11-poly-ws-2', title: 'Synthetic Division & Remainder Theorem', description: 'Use synthetic division and apply the Remainder and Factor Theorems', topic: 'Polynomials', difficulty: 'Medium', pages: 4 },
  { id: 'g11-poly-ws-3', title: 'Zeros of Polynomial Functions', description: 'Find rational zeros, apply Descartes Rule of Signs, and analyze end behavior', topic: 'Polynomials', difficulty: 'Hard', pages: 5 },
  { id: 'g11-poly-ws-4', title: 'Factoring Higher-Degree Polynomials', description: 'Factor sum/difference of cubes, factor by grouping, and fully factor degree-3 and degree-4 polynomials', topic: 'Polynomials', difficulty: 'Hard', pages: 5 },

  // Rational Expressions & Equations
  { id: 'g11-rat-ws-1', title: 'Simplifying Rational Expressions', description: 'Factor and simplify rational expressions, identify restrictions on the domain', topic: 'Rational Expressions', difficulty: 'Medium', pages: 4 },
  { id: 'g11-rat-ws-2', title: 'Multiplying & Dividing Rational Expressions', description: 'Multiply and divide rational expressions by factoring and canceling common factors', topic: 'Rational Expressions', difficulty: 'Medium', pages: 4 },
  { id: 'g11-rat-ws-3', title: 'Adding & Subtracting Rational Expressions', description: 'Find LCD and add or subtract rational expressions with unlike denominators', topic: 'Rational Expressions', difficulty: 'Hard', pages: 5 },
  { id: 'g11-rat-ws-4', title: 'Solving Rational Equations', description: 'Solve rational equations and identify extraneous solutions', topic: 'Rational Expressions', difficulty: 'Hard', pages: 5 },
  { id: 'g11-rat-ws-5', title: 'Asymptotes & Graphing Rational Functions', description: 'Find vertical, horizontal, and oblique asymptotes and sketch rational function graphs', topic: 'Rational Expressions', difficulty: 'Hard', pages: 5 },

  // Exponential & Logarithmic Functions
  { id: 'g11-exp-ws-1', title: 'Exponential Growth & Decay', description: 'Model and solve exponential growth and decay problems including half-life and compound interest', topic: 'Exponential & Logarithms', difficulty: 'Medium', pages: 4 },
  { id: 'g11-exp-ws-2', title: 'Logarithm Properties & Expansion', description: 'Apply product, quotient, and power rules to expand and condense logarithmic expressions', topic: 'Exponential & Logarithms', difficulty: 'Medium', pages: 4 },
  { id: 'g11-exp-ws-3', title: 'Solving Exponential & Logarithmic Equations', description: 'Solve equations involving exponential and logarithmic expressions using properties and change of base', topic: 'Exponential & Logarithms', difficulty: 'Hard', pages: 5 },
  { id: 'g11-exp-ws-4', title: 'Graphing Exponential & Log Functions', description: 'Graph transformations of exponential and logarithmic functions, identify asymptotes and key features', topic: 'Exponential & Logarithms', difficulty: 'Medium', pages: 4 },

  // Sequences, Series & Binomial Theorem
  { id: 'g11-seq-ws-1', title: 'Arithmetic Sequences & Series', description: 'Find terms, common differences, and sums of arithmetic sequences and series', topic: 'Sequences & Series', difficulty: 'Easy', pages: 3 },
  { id: 'g11-seq-ws-2', title: 'Geometric Sequences & Series', description: 'Find terms, common ratios, and sums of finite and infinite geometric series', topic: 'Sequences & Series', difficulty: 'Medium', pages: 4 },
  { id: 'g11-seq-ws-3', title: 'Sigma Notation & Series Sums', description: 'Evaluate sums written in sigma notation and convert between sigma and expanded form', topic: 'Sequences & Series', difficulty: 'Medium', pages: 4 },
  { id: 'g11-seq-ws-4', title: 'Binomial Theorem & Pascal\'s Triangle', description: 'Expand binomial expressions using the Binomial Theorem and Pascal\'s Triangle', topic: 'Sequences & Series', difficulty: 'Hard', pages: 5 },

  // Trigonometry & Unit Circle
  { id: 'g11-trig-ws-1', title: 'Unit Circle & Radian Measure', description: 'Convert between degrees and radians, identify coordinates on the unit circle', topic: 'Trigonometry', difficulty: 'Medium', pages: 4 },
  { id: 'g11-trig-ws-2', title: 'Trigonometric Identities', description: 'Verify and apply Pythagorean, reciprocal, quotient, and co-function identities', topic: 'Trigonometry', difficulty: 'Hard', pages: 5 },
  { id: 'g11-trig-ws-3', title: 'Solving Trigonometric Equations', description: 'Solve trig equations over specified intervals and find general solutions', topic: 'Trigonometry', difficulty: 'Hard', pages: 5 },
  { id: 'g11-trig-ws-4', title: 'Graphing Trig Functions & Transformations', description: 'Graph sine, cosine, and tangent functions with amplitude, period, phase shift, and vertical shift', topic: 'Trigonometry', difficulty: 'Medium', pages: 4 },

  // Statistics & Probability
  { id: 'g11-stat-ws-1', title: 'Normal Distribution & Z-Scores', description: 'Interpret normal distributions, calculate z-scores, and use the empirical rule', topic: 'Statistics & Probability', difficulty: 'Medium', pages: 4 },
  { id: 'g11-stat-ws-2', title: 'Conditional Probability & Independence', description: 'Calculate conditional probability, test for independence, and use multiplication rules', topic: 'Statistics & Probability', difficulty: 'Hard', pages: 5 },
  { id: 'g11-stat-ws-3', title: 'Permutations & Combinations', description: 'Count arrangements using permutations and combinations with factorial notation', topic: 'Statistics & Probability', difficulty: 'Medium', pages: 4 },
  { id: 'g11-stat-ws-4', title: 'Data Analysis & Statistical Inference', description: 'Analyze data sets, interpret standard deviation, and draw conclusions from samples', topic: 'Statistics & Probability', difficulty: 'Medium', pages: 4 },

  // Conic Sections
  { id: 'g11-conic-ws-1', title: 'Circles & Parabolas', description: 'Write equations of circles and parabolas in standard form and graph them', topic: 'Conic Sections', difficulty: 'Medium', pages: 4 },
  { id: 'g11-conic-ws-2', title: 'Ellipses & Hyperbolas', description: 'Write equations of ellipses and hyperbolas, identify vertices, foci, and asymptotes', topic: 'Conic Sections', difficulty: 'Hard', pages: 5 },
  { id: 'g11-conic-ws-3', title: 'Completing the Square for Conics', description: 'Convert general form to standard form by completing the square for all conic sections', topic: 'Conic Sections', difficulty: 'Hard', pages: 5 },
];

const categories = [
  { id: 'Polynomials', name: 'Polynomials', color: 'bg-rose-500', lightColor: 'bg-rose-50' },
  { id: 'Rational Expressions', name: 'Rational Expressions', color: 'bg-amber-500', lightColor: 'bg-amber-50' },
  { id: 'Exponential & Logarithms', name: 'Exponential & Logarithms', color: 'bg-teal-500', lightColor: 'bg-teal-50' },
  { id: 'Sequences & Series', name: 'Sequences & Series', color: 'bg-violet-500', lightColor: 'bg-violet-50' },
  { id: 'Trigonometry', name: 'Trigonometry', color: 'bg-sky-500', lightColor: 'bg-sky-50' },
  { id: 'Statistics & Probability', name: 'Statistics & Probability', color: 'bg-green-500', lightColor: 'bg-green-50' },
  { id: 'Conic Sections', name: 'Conic Sections', color: 'bg-orange-500', lightColor: 'bg-orange-50' },
];

export default function Grade11ProgressPage() {
  const [progress, setProgress] = useState<WorksheetProgress>({});
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('grade-11-progress');
    if (saved) {
      setProgress(JSON.parse(saved));
    }
  }, []);

  const toggleWorksheet = (id: string) => {
    const newProgress = { ...progress, [id]: !progress[id] };
    setProgress(newProgress);
    localStorage.setItem('grade-11-progress', JSON.stringify(newProgress));
  };

  const resetProgress = () => {
    setProgress({});
    localStorage.removeItem('grade-11-progress');
    setShowResetModal(false);
  };

  const totalWorksheets = worksheets.length;
  const completedWorksheets = Object.values(progress).filter(Boolean).length;
  const completionPercentage = Math.round((completedWorksheets / totalWorksheets) * 100);

  const getCategoryProgress = (categoryId: string) => {
    const categoryWorksheets = worksheets.filter(w => w.topic === categoryId);
    const completed = categoryWorksheets.filter(w => progress[w.id]).length;
    return {
      completed,
      total: categoryWorksheets.length,
      percentage: categoryWorksheets.length > 0 ? Math.round((completed / categoryWorksheets.length) * 100) : 0
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-rose-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <i className="ri-bar-chart-box-line text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">Grade 11 Progress Tracker</h1>
                  <p className="text-slate-600 mt-1">Track your mastery across all 27 worksheets</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowResetModal(true)}
              className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-restart-line"></i>
              Reset Progress
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Overall Progress Ring */}
        <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Overall Progress</h2>
              <p className="text-rose-50 mb-4">Keep going! You're making great progress on your Grade 11 math mastery.</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold">{completedWorksheets}</span>
                <span className="text-2xl text-rose-100">/ {totalWorksheets}</span>
                <span className="text-rose-100 ml-2">worksheets completed</span>
              </div>
            </div>
            <div className="relative w-48 h-48">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="16"
                  fill="none"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="white"
                  strokeWidth="16"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 88}`}
                  strokeDashoffset={`${2 * Math.PI * 88 * (1 - completionPercentage / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl font-bold">{completionPercentage}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Category Progress Cards */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Progress by Topic</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map(category => {
              const categoryProgress = getCategoryProgress(category.id);
              return (
                <div key={category.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center`}>
                      <i className="ri-file-list-3-line text-xl text-white"></i>
                    </div>
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-slate-900">{categoryProgress.completed}</span>
                      <span className="text-slate-500">/ {categoryProgress.total}</span>
                      <span className="text-sm text-slate-500 ml-auto">{categoryProgress.percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`${category.color} h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${categoryProgress.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Worksheet Checklist */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Worksheet Checklist</h2>
          
          {categories.map(category => {
            const categoryWorksheets = worksheets.filter(w => w.topic === category.id);
            if (categoryWorksheets.length === 0) return null;

            return (
              <div key={category.id} className="mb-8 last:mb-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 ${category.color} rounded-lg flex items-center justify-center`}>
                    <i className="ri-folder-line text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{category.name}</h3>
                  <span className="text-sm text-slate-500 ml-2">
                    ({getCategoryProgress(category.id).completed}/{categoryWorksheets.length})
                  </span>
                </div>
                
                <div className="space-y-3">
                  {categoryWorksheets.map(worksheet => {
                    const isCompleted = progress[worksheet.id];
                    return (
                      <div
                        key={worksheet.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                          isCompleted
                            ? `${category.lightColor} border-${category.color.replace('bg-', '')}`
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <button
                          onClick={() => toggleWorksheet(worksheet.id)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                            isCompleted
                              ? `${category.color} border-transparent`
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                        >
                          {isCompleted && <i className="ri-check-line text-white text-sm"></i>}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold mb-1 ${isCompleted ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                            {worksheet.title}
                          </h4>
                          <p className={`text-sm ${isCompleted ? 'line-through text-slate-400' : 'text-slate-600'}`}>
                            {worksheet.description}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm text-slate-500">{worksheet.pages} pages</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                            worksheet.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {worksheet.difficulty}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <a
            href="/grade/11"
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-book-open-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Grade 11 Curriculum</h3>
            <p className="text-slate-600 text-sm">Review all topics and practice questions</p>
          </a>

          <a
            href="/grade-11-worksheets"
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-file-list-3-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Browse Worksheets</h3>
            <p className="text-slate-600 text-sm">Find worksheets by topic and difficulty</p>
          </a>

          <a
            href="/grade-11-study-plan"
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group cursor-pointer"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-calendar-check-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Study Plan</h3>
            <p className="text-slate-600 text-sm">Create a personalized prep schedule</p>
          </a>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-3xl text-red-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-2">Reset Progress?</h3>
            <p className="text-slate-600 text-center mb-6">
              This will clear all your completed worksheets. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={resetProgress}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Reset All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}